<template>
  <div class="p-2 rounded-md bg-gray-200 self-start">
    <div class="flex flex-row">
      <div class="flex gap-2">
        <button class="p-2 rounded-md bg-gray-300 hover:bg-gray-100 disabled:bg-gray-200 disabled:hover:bg-gray-200 disabled:text-gray-300"
          :disabled="disableChangeStatusButton"
          @click="onNotifyStatusSelected">
          Notification Menu
        </button>
        <button class="p-2 rounded-md bg-gray-300 hover:bg-gray-100 disabled:bg-gray-200 disabled:hover:bg-gray-200 disabled:text-gray-300"
          :disabled="disableChangeStatusButton"
          @click="onChangeStatusSelected">
          Change Status
        </button>
      </div>
    </div>
    <Modal
      ref="changeStatusModal"
      :size="changeStatusModalSize"
      :width="changeStatusModalWidth"
      :onClose="onChangeStatusClose"
      anchor="mid">
      <template #title>
        <h2 class="p-2 text-center mx-auto">Confirm Change Status</h2>
      </template>
      <template #content>
        <div ref="view">
          <select class="p-2"
            v-model="statusSelected"
            @change="onChangeStatusFromUser">
            <option
              v-for="kv in options"
              :key="kv.value"
              :value="kv.value">{{ kv.label }}
            </option>
          </select>
          <div class="flex flex-col"
            v-if="showActiveSyncLogging">
            <span class="pt-4 px-2 text-sm border-b text-gray-400">Active Sync Management</span>
            <SyncStepDisplay
              :wait="serverCommunication.waitOnActiveSyncData"
              :syncIds="syncStepIds"
            />
          </div>
          <div class="flex flex-col"
            v-if="componentSelected === 'routed'">
            <div class="flex flex-col"
              v-if="showFutureSchedule">
              <span class="pt-4 px-2 text-sm border-b text-gray-400">Future Schedule</span>
              <FutureScheduleDisplay
                :wait="serverCommunication.waitOnActiveSyncData"
                :studentDbId="studentDbId"
                :scheduleRequestId="scheduleRequestId"
                :existingFutureScheduleId="existingFutureScheduleId"
                @loadSchedule="onLoadNewSchedule"
                @loadScheduleComplete="onLoadNewScheduleComplete"
                @validateChanges="onValidateChanges"
              />
            </div>
            <div class="flex flex-col"
              v-if="showRoutePullingStatus">
              <span class="pt-4 px-2 text-sm border-b text-gray-400">Route Pulling</span>
              <RoutePullStatus
                :wait="serverCommunication.waitOnActiveSyncData"
                :statusObjects="routePullSource"
                :routePullDataArray="routePullDataArray"
                @addItem="onAddItemToRoutePulling"
                @removeItem="onRemoveItemToRoutePulling"
              />
            </div>
          </div>
          <div class="flex flex-col"
            v-if="showScheduleRequestControlPanel &&
              (statusSelected === 'routing' || statusSelected === 'waiting_additional_info')"
          >
            <span class="pt-4 px-2 text-sm border-b text-gray-400">Schedule Request Management</span>
            <SingleScheduleRequestController
              :wait="serverCommunication.waitOnActiveSyncData"
              :scheduleRequestId="scheduleRequestId"
              :showRequestData="false"
            />
          </div>
          <div
            v-if="serverCommunication.waitOnServerResponse">
            <div class="flex flex-col items-center opacity-75 top-1/2 my-auto mx-auto block relative w-16 h-16" role="status">
              <div class="flex-none spinner-border animate-spin inline-block w-16 h-16 border-4 rounded-full" role="status"></div>
              <span class="flex top-1/2 pt-2 my-0 mx-auto block text-center font-italic">
                Updating
              </span>
            </div>
          </div>
          <div class="flex p-4 w-full justify-center"
            v-if="serverCommunication.displayErrorSavingStatus">
            <span class="text-red-400">
              Error updating status for request.
            </span>
          </div>
        </div>
      </template>
      <template #footer="slotProps">
        <Button
          type="button"
          color="blue"
          :disabled="
            serverCommunication.waitOnServerResponse
            || (futureChangesValidationRequired && !futureChangesValidated)
          "
          class="rounded-md font-medium px-3 py-2 shadow-sm"
          @click="onChangeStatusConfirmClick(slotProps)">
          Confirm
        </Button>
      </template>
    </Modal>
    <Modal
      ref="notifyStatusModal"
      size="sm"
      anchor="mid">
      <template #title>
        <h2 class="p-2 text-center mx-auto">Actions for Notification</h2>
      </template>
      <template #content>
        <div class="flex p-4 w-full justify-center"
          v-if="notifyStatusModel.errors.errorGettingData">
          <span class="text-red-400">
            Error acquiring Route Notification data from server.
          </span>
        </div>
        <div class="flex flex-col">
          <div class="m-4">
            <div class="flex flex-col">
              <div class="flex flex-row rounded-sm p-2 mb-2 bg-gray-100"
                v-if="notifyStatusModel.markedForNotification">
                <svg
                  width="24px"
                  height="24px"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg">
                  <polygon
                    fill-rule="evenodd"
                    points="9.707 14.293 19 5 20.414 6.414 9.707 17.121 4 11.414 5.414 10"
                  />
                </svg>
                <span>
                  Will be notified tonight (4 PM Central)
                </span>
                <div class="flex grow justify-end items-center">
                  <TrashButton class="bg-orange-200 hover:bg-orange-400 rounded-sm p-1 w-5 h-5 justify-center"
                    @click="clearNotification"
                  />
                </div>
              </div>
              <span class="p-2">
                Last notification: {{ lastNotificationCreatedAtDisplay }}
              </span>
              <Button
                :disabled="notifyStatusModel.lastNotification == undefined"
                type="submit"
                color="gray"
                class="p-0.5 disabled:hover:bg-gray-50 disabled:bg-gray-50 disabled:text-gray-300"
                @click="toggleViewLastNotification">
                View
              </button>
              <CollapseVertical
                :open="notifyStatusModel.openLastNotification"
                v-if="lastNotificationContent !== undefined">
                <div class="flex flex-col max-h-112 overflow-y-auto">
                  <div class="flex flex-col p-2 w-full">
                    <div class="border-b-2">
                      Accomodations
                    </div>
                    <div class="grid grid-cols-2 p-2">
                      <span class="text-sm truncate"
                        v-for="accommodation in lastNotificationContent.accommodations"
                        :key="accommodation">
                        {{ accommodation }}
                      </span>
                    </div>
                  </div>
                  <div class="flex flex-col p-2 w-full">
                    <div class="border-b-2">
                      Effective Date
                    </div>
                    <span class="text-sm ml-2">
                      {{ lastNotificationContent.effectiveDate }}
                    </span>
                  </div>
                  <div v-if="lastNotificationContent.dayData.dayVariant">
                    <table>
                      <tr
                        v-for="day in lastNotificationContent.dayData.listDays"
                        :key="day.weekday">
                        <td>
                          {{ day.weekday }}
                        </td>
                        <td>
                          <div class="flex flex-col p-2 w-full"
                            v-if="day.pickupBuilding && day.pickupVehicleCode">
                            <span class="text-sm ml-2 w-full border-b-2">
                              Pick-Up
                            </span>
                            <span class="text-sm ml-2">
                              School Name: {{ day.pickupBuilding.name }}
                            </span>
                            <span class="text-sm ml-2">
                              Bus No.: {{ day.pickupVehicleCode }}
                            </span>
                            <span class="text-sm ml-2">
                              Pickup Time: {{ day.pickupTime }}
                            </span>
                            <span class="text-sm ml-2"
                              v-if="day.pickupBusStopDescription !== undefined">
                              Bus Stop: {{ day.pickupBusStopDescription }}
                            </span>
                            <span class="text-sm ml-2"
                              v-else>
                              Address: {{ day.pickupAddressDisplay }}
                            </span>
                          </div>
                        </td>
                        <td>
                          <div class="flex flex-col p-2 w-full"
                            v-if="day.dropoffBuilding && day.dropoffVehicleCode">
                            <span class="text-sm ml-2 w-full border-b-2">
                              Drop-Off
                            </span>
                            <span class="text-sm ml-2">
                              School Name: {{ day.dropoffBuilding.name }}
                            </span>
                            <span class="text-sm ml-2">
                              Bus No.: {{ day.dropoffVehicleCode }}
                            </span>
                            <span class="text-sm ml-2">
                              Dropoff Time: {{ day.dropoffTime }}
                            </span>
                            <span class="text-sm ml-2"
                              v-if="day.dropoffBusStopDescription !== undefined">
                              Bus Stop: {{ day.dropoffBusStopDescription }}
                            </span>
                            <span class="text-sm ml-2"
                              v-else>
                              Address: {{ day.dropoffAddressDisplay }}
                            </span>
                          </div>
                        </td>
                      </tr>
                    </table>
                  </div>
                  <div v-else>
                    <div class="flex flex-col p-2 w-full">
                      <div class="border-b-2">
                        Days
                      </div>
                      <div class="flex flex-row">
                        <span class="text-sm ml-2"
                          v-for="weekday in lastNotificationContent.dayData.weekdays"
                          :key="weekday">
                          {{ weekday }}
                        </span>
                      </div>
                    </div>
                    <div class="flex flex-col p-2 w-full"
                      v-if="lastNotificationContent.dayData.day.pickupVehicleCode">
                      <div class="border-b-2 mb-2">
                        Pick-Up
                      </div>
                      <span class="text-sm ml-2"
                        v-if="lastNotificationContent.dayData.day.pickupBuilding">
                        School Name: {{ lastNotificationContent.dayData.day.pickupBuilding.name }}
                      </span>
                      <span class="text-sm ml-2"
                        v-else>
                        Error Loading School Name (School Code: {{ lastNotificationContent.dayData.day.pickupBuildingCode }})
                      </span>
                      <span class="text-sm ml-2">
                        Bus No.: {{ lastNotificationContent.dayData.day.pickupVehicleCode }}
                      </span>
                      <span class="text-sm ml-2">
                        Pickup Time: {{ lastNotificationContent.dayData.day.pickupTime }}
                      </span>
                      <span class="text-sm ml-2"
                        v-if="lastNotificationContent.dayData.day.pickupBusStopDescription !== undefined">
                        Bus Stop: {{ lastNotificationContent.dayData.day.pickupBusStopDescription }}
                      </span>
                      <span class="text-sm ml-2"
                        v-else>
                        Address: {{ lastNotificationContent.dayData.day.pickupAddressDisplay }}
                      </span>
                    </div>
                    <div class="flex flex-col p-2 w-full"
                      v-if="lastNotificationContent.dayData.day.dropoffVehicleCode">
                      <div class="border-b-2 mb-2">
                        Drop-Off
                      </div>
                      <span class="text-sm ml-2"
                        v-if="lastNotificationContent.dayData.day.dropoffBuilding">
                        School Name: {{ lastNotificationContent.dayData.day.dropoffBuilding.name }}
                      </span>
                      <span class="text-sm ml-2"
                        v-else>
                        Error Loading School Name (School Code: {{ lastNotificationContent.dayData.day.dropoffBuildingCode }})
                      </span>
                      <span class="text-sm ml-2">
                        Bus No.: {{ lastNotificationContent.dayData.day.dropoffVehicleCode }}
                      </span>
                      <span class="text-sm ml-2">
                        Dropoff Time: {{ lastNotificationContent.dayData.day.dropoffTime }}
                      </span>
                      <span class="text-sm ml-2"
                        v-if="lastNotificationContent.dayData.day.dropoffBusStopDescription !== undefined">
                        Bus Stop: {{ lastNotificationContent.dayData.day.dropoffBusStopDescription }}
                      </span>
                      <span class="text-sm ml-2"
                        v-else>
                        Address: {{ lastNotificationContent.dayData.day.dropoffAddressDisplay }}
                      </span>
                    </div>
                  </div>
                </div>
              </CollapseVertical>
            </div>
          </div>
        </div>
        <div class="flex flex-col">
          <div class="mt-4 ml-4 w-1/2">
            <Button
              type="submit"
              color="gray"
              size="md"
              class="mx-2 mt-2 mb-4 rounded-md w-full !shadow disabled:bg-gray-100 disabled:text-gray-400"
              :disabled="statusFromServer !== 'routed'"
              @click="onNotifyImmediateClick">
              Notify Immediately
            </Button>
          </div>
        </div>
        <div
          v-if="serverCommunication.waitOnServerResponse">
          <div class="flex flex-col items-center opacity-75 top-1/2 my-auto mx-auto block relative w-16 h-16" role="status">
            <div class="flex-none spinner-border animate-spin inline-block w-16 h-16 border-4 rounded-full" role="status"></div>
            <span class="flex top-1/2 pt-2 my-0 mx-auto block text-center font-italic">
              Updating
            </span>
          </div>
        </div>
        <div class="flex p-4 w-full justify-center"
          v-if="serverCommunication.displayErrorSavingStatus">
          <span class="text-red-400">
            Error updating status for request.
          </span>
        </div>
      </template>
      <template #footer="slotProps">
        <Button
          type="button"
          color="blue"
          :disabled="serverCommunication.waitOnServerResponse || statusFromServer !== 'routed'"
          class="rounded-md w-1/2 rounded-md font-medium px-3 py-2 shadow-sm"
          @click="onNotifyConfirmClick(slotProps)">
          Notify Tonight (4 PM Central)
        </Button>
      </template>
    </Modal>
    <NotifyImmediate
      :show="notifyImmediateShow"
      @update:show="newValue => notifyImmediateShow = newValue"
      :scheduleRequestId="scheduleRequestId"
      @notifiedImmediate="onNotifyImmediate"
    />
  </div>
</template>

<script>
import Modal from '../Modal.vue'
// import Input from '../Input.vue'
import Button from '../Button.vue'
import TrashButton from '../TrashButton.vue'
import CollapseVertical from '../CollapseVertical.vue'

import NotifyImmediate from './NotifyImmediate.vue'
import FutureScheduleDisplay from './FutureScheduleDisplay.vue'

import SingleScheduleRequestController from '../routingManagement/StudentRequests/SingleScheduleRequestController.vue'
import SyncStepDisplay from '../routingManagement/SyncStep/SyncStepDisplay.vue'
import RoutePullStatus from '../routingManagement/routePulling/RoutePullStatus.vue'

import DROPDOWN_LISTS from '../DropdownLists.json'
import { getRouteNotificationInfo, postRouteNotificationInfo } from '../../services/api.js'
import scheduleRequestService from '../../services/activeSync/scheduleRequest.js'

function addressEquals(a, b) {
  if (a == undefined) {
    if (b == undefined) {
      return true
    }
    else {
      return false
    }
  }
  else {
    if (b == undefined) {
      return false
    }
    else {
      return a.apartment === b.apartment && a.city === b.city && a.state === b.state && a.street === b.street && a.zip_code === b.zip_code
    }
  }
}

export default {
  components: {
    Modal,
    // Input,
    Button,
    TrashButton,
    CollapseVertical,
    NotifyImmediate,
    FutureScheduleDisplay,
    SingleScheduleRequestController,
    SyncStepDisplay,
    RoutePullStatus,
  },
  props: {
    statusFromServer: String,
    studentDbId: Number,
    scheduleRequestId: Number,
  },
  data() {
    return {
      hideSection: true,

      options: [
        {
          value: 'received',
          label: 'Received',
          statusColor: 'rgb(234 234 34)',
        },
        {
          value: 'routing',
          label: 'Processing',
        },
        {
          value: 'waiting_additional_info',
          label: 'Waiting on Additional Info',
        },
        {
          value: 'routed',
          label: 'Processed',
        },
      ],
      statusSelected: 'received',
      previousStatusSelected: undefined,
      componentSelected: '',
      serverCommunication: {
        waitOnServerResponse: false,
        displayErrorSavingStatus: false,
        waitOnLoadingNewSchedule: false,
        waitOnActiveSyncData: false,
      },
      changeStatusModalSize: 'sm',
      changeStatusModalWidth: '1',

      futureChangesValidated: false,
      futureChangesValidationRequired: false,

      activeSyncData: { data: undefined },

      inputModels: {
        additionalInfo: {
          sendMessage: '',
          email: '',
        },
      },
      notifyStatusModel: {
        errors: {
          errorGettingData: false,
          errorSendingNotification: false,
        },
        lastNotification: undefined,
        openLastNotification: false,
        markedForNotification: false,
        forceNotify: false,
      },
      notifyImmediateShow: false,
    }
  },
  methods: {
    async getActiveSyncData(newStatus) {
      this.serverCommunication.waitOnActiveSyncData = true
      try {
        this.activeSyncData = await scheduleRequestService.getAllData(this.scheduleRequestId, newStatus)
      }
      finally {
        this.serverCommunication.waitOnActiveSyncData = false
      }
    },
    getRouteNotificationInfo() {
      this.serverCommunication.waitOnServerResponse = true
      getRouteNotificationInfo(this.scheduleRequestId)
        .then(result => {
          this.serverCommunication.waitOnServerResponse = false
          if (result.status === 200) {
            let body = result.data
            this.notifyStatusModel.lastNotification = body.last_notification
            this.notifyStatusModel.markedForNotification = body.marked_for_notification
          }
          else {
            this.notifyStatusModel.errors.errorGettingData = true
          }
        })
        .catch(() => {
          this.serverCommunication.waitOnServerResponse = false
          this.notifyStatusModel.errors.errorGettingData = true
        })
    },
    onChangeStatusFromUser() {
      this.getActiveSyncData(this.statusSelected)
    },
    onChangeStatusSelected() {
      this.getActiveSyncData(this.statusSelected)
      this.serverCommunication.waitOnServerResponse = false
      this.serverCommunication.displayErrorSavingStatus = false
      this.$refs.changeStatusModal.openModal()
    },
    onNotifyStatusSelected() {
      this.getRouteNotificationInfo()
      this.notifyImmediateShow = false
      this.serverCommunication.waitOnServerResponse = false
      this.serverCommunication.displayErrorSavingStatus = false
      this.$refs.notifyStatusModal.openModal()
    },
    onLoadNewSchedule() {
      this.changeStatusModalSize = 'md'
      this.changeStatusModalWidth = '6'
      this.serverCommunication.waitOnLoadingNewSchedule = true
    },
    onLoadNewScheduleComplete() {
      this.serverCommunication.waitOnLoadingNewSchedule = false
    },
    onValidateChanges() {
      this.futureChangesValidated = true
    },
    async onAddItemToRoutePulling(statusObject) {
      if (statusObject.type === 'schedule_request') {
        this.activeSyncData.data['route_pulling'] = await scheduleRequestService.setRoutePullScheduleRequest(statusObject.id, true)
      }
    },
    async onRemoveItemToRoutePulling(statusObject) {
      if (statusObject.type === 'schedule_request') {
        this.activeSyncData.data['route_pulling'] = await scheduleRequestService.setRoutePullScheduleRequest(statusObject.id, false)
      }
    },
    onChangeStatusClose() {
      this.changeStatusModalSize = 'sm'
      this.changeStatusModalWidth = '1'
    },
    async onChangeStatusConfirmClick(slotProps) {
      this.serverCommunication.waitOnServerResponse = true
      this.serverCommunication.displayErrorSavingStatus = false
      try {
        let success = await scheduleRequestService.setScheduleRequestStatus(this.scheduleRequestId, this.statusSelected)
        if (success) {
          this.serverCommunication.waitOnServerResponse = false
          this.previousStatusSelected = this.statusSelected
          this.$emit('reloadStatus')
          slotProps.closeModal(true)

          this.getRouteNotificationInfo()
        }
        else {
          this.serverCommunication.displayErrorSavingStatus = true
        }
      }
      catch (error) {
        this.serverCommunication.displayErrorSavingStatus = true
        this.statusSelected = this.previousStatusSelected
        throw error
      }
      finally {
        this.serverCommunication.waitOnServerResponse = false
      }
    },
    async onNotifyConfirmClick() {
      this.serverCommunication.waitOnServerResponse = false
      this.serverCommunication.displayErrorSavingStatus = false
      try {
        let result = await postRouteNotificationInfo({
            action: 'mark_for_notification',
            options: {},
          }, this.scheduleRequestId)

        if (result.status == 200) {
          this.serverCommunication.waitOnServerResponse = false
          this.notifyStatusModel.markedForNotification = true
        }
        else {
          this.serverCommunication.displayErrorSavingStatus = true
        }
      }
      catch (error) {
        this.serverCommunication.displayErrorSavingStatus = true
        throw error
      }
    },
    onNotifyImmediate() {
      this.getRouteNotificationInfo()
    },
    onNotifyImmediateClick() {
      this.notifyImmediateShow = true
    },
    toggleViewLastNotification() {
      this.notifyStatusModel.openLastNotification = !this.notifyStatusModel.openLastNotification
    },
    async clearNotification() {
      try {
        let result = await postRouteNotificationInfo({
            action: 'clear_notification',
            options: {},
          }, this.scheduleRequestId)

        if (result.status == 200) {
          this.serverCommunication.waitOnServerResponse = false
          this.notifyStatusModel.markedForNotification = false
        }
        else {
          this.serverCommunication.displayErrorSavingStatus = true
        }
      }
      catch (error) {
        this.serverCommunication.displayErrorSavingStatus = true
        throw error
      }
    }
  },
  watch: {
    statusSelected(newValue) {
      this.componentSelected = newValue
      if (!this.showFutureSchedule) {
        return
      }
      if (newValue === 'routed') {
        this.futureChangesValidationRequired = true
      }
      else {
        this.futureChangesValidationRequired = false
      }
    },
    scheduleRequestId(newValue) {
      if (newValue < 0) {
        return
      }

      this.getActiveSyncData()
      this.getRouteNotificationInfo()
    },
    statusFromServer(newValue) {
      this.statusSelected = newValue
      this.previousStatusSelected = newValue
    },
  },
  computed: {
    disableChangeStatusButton() {
      return this.scheduleRequestId === -1 || this.statusFromServer === 'unknown'
    },
    syncStepIds() {
      if (this.activeSyncData?.data == undefined || this.activeSyncData?.data['sync_management'] == undefined) {
        return []
      }
      return this.activeSyncData.data['sync_management']?.ids
    },
    existingFutureScheduleId() {
      if (this.activeSyncData?.data == undefined || this.activeSyncData?.data['future_schedule'] == undefined) {
        return undefined
      }
      return this.activeSyncData.data['future_schedule']?.id
    },
    routePullDataArray() {
      if (this.activeSyncData?.data == undefined || this.activeSyncData?.data['route_pulling'] == undefined) {
        return undefined
      }
      if (!this.activeSyncData?.data['route_pulling']?.exists) {
        return undefined
      }
      return [this.activeSyncData.data['route_pulling']?.data]
    },
    routePullSource() {
      return {
        schedule_request: {
          id: this.scheduleRequestId,
        },
      }
    },
    showActiveSyncLogging() {
      return false
    },
    showScheduleRequestControlPanel() {
      return false
    },
    showFutureSchedule() {
      return false
    },
    showRoutePullingStatus() {
      return false
    },
    lastNotificationCreatedAtDisplay() {
      if (this.notifyStatusModel.lastNotification == undefined) return ''

      return new Date(this.notifyStatusModel.lastNotification.created_at).toLocaleString('en-US', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    lastNotificationContent() {
      if (this.notifyStatusModel.lastNotification == undefined) return undefined

      let content = {}
      let dayVariant = false
      if (this.notifyStatusModel.lastNotification.route_notification_weekdays.length > 0) {
        let firstDay = this.notifyStatusModel.lastNotification.route_notification_weekdays[0]
        let dayData = {
          pickupVehicleCode: firstDay.pickup_vehicle_code,
          dropoffVehicleCode: firstDay.dropoff_vehicle_code,
          pickupBuildingCode: firstDay.pickup_building_code,
          dropoffBuildingCode: firstDay.dropoff_building_code,
          pickupBuilding: firstDay.pickup_building,
          dropoffBuilding: firstDay.dropoff_building,
          pickupTime: firstDay.pickup_time,
          dropoffTime: firstDay.dropoff_time,
          pickupBusStopDescription: firstDay.pickup_bus_stop_description,
          dropoffBusStopDescription: firstDay.dropoff_bus_stop_description,
          pickupAddress: firstDay.pickup_address,
          dropoffAddress: firstDay.dropoff_address,
        }

        for (const notiDay of this.notifyStatusModel.lastNotification.route_notification_weekdays) {
          if (dayData.pickupVehicleCode != notiDay.pickup_vehicle_code) dayVariant = true
          if (dayData.dropoffVehicleCode != notiDay.dropoff_vehicle_code) dayVariant = true
          if (dayData.pickupBuildingCode != notiDay.pickup_building_code) dayVariant = true
          if (dayData.dropoffBuildingCode != notiDay.dropoff_building_code) dayVariant = true
          if (dayData.pickupTime != notiDay.pickup_time) dayVariant = true
          if (dayData.dropoffTime != notiDay.dropoff_time) dayVariant = true
          if (dayData.pickupBusStopDescription != notiDay.pickup_bus_stop_description) dayVariant = true
          if (dayData.dropoffBusStopDescription != notiDay.dropoff_bus_stop_description) dayVariant = true
          if (!addressEquals(dayData.pickupAddress, notiDay.pickup_address)) dayVariant = true
          if (!addressEquals(dayData.dropoffAddress, notiDay.dropoff_address)) dayVariant = true

          if (dayVariant === true) break
        }

        if (dayVariant) {
          let listDays = []
          for (const notiDay of this.notifyStatusModel.lastNotification.route_notification_weekdays) {
            listDays.push({
              weekday: DROPDOWN_LISTS.weekdays[notiDay.weekday],
              pickupVehicleCode: notiDay.pickup_vehicle_code,
              dropoffVehicleCode: notiDay.dropoff_vehicle_code,
              pickupBuildingCode: notiDay.pickup_building_code,
              dropoffBuildingCode: notiDay.dropoff_building_code,
              pickupBuilding: notiDay.pickup_building,
              dropoffBuilding: notiDay.dropoff_building,
              pickupTime: (notiDay.pickup_time) ?
                new Date(`1970-01-01T${notiDay.pickup_time}.000Z`).toLocaleString('en-US', {
                  hour: 'numeric',
                  minute: 'numeric',
                  hour12: true,
                  timeZone: 'UTC',
                }) : undefined,
              dropoffTime: (notiDay.dropoff_time) ?
                new Date(`1970-01-01T${notiDay.dropoff_time}.000Z`).toLocaleString('en-US', {
                  hour: 'numeric',
                  minute: 'numeric',
                  hour12: true,
                  timeZone: 'UTC',
                }) : undefined,
              pickupBusStopDescription: notiDay.pickup_bus_stop_description,
              dropoffBusStopDescription: notiDay.dropoff_bus_stop_description,
              pickupAddress: notiDay.pickup_address,
              dropoffAddress: notiDay.dropoff_address,
            })
          }
          content.dayData = {
            dayVariant: true,
            listDays: listDays,
          }
        }
        else {
          let weekdays = this.notifyStatusModel.lastNotification.route_notification_weekdays.map(notiDay => DROPDOWN_LISTS.weekdays[notiDay.weekday])
          if (dayData.pickupAddress) {
            if (dayData.pickupAddress.apartment) {
              dayData.pickupAddressDisplay = `${dayData.pickupAddress.street}, ${dayData.pickupAddress.apartment}, ${dayData.pickupAddress.city}, ${dayData.pickupAddress.state}, ${dayData.pickupAddress.zip_code}`
            }
            else {
              dayData.pickupAddressDisplay = `${dayData.pickupAddress.street}, ${dayData.pickupAddress.city}, ${dayData.pickupAddress.state}, ${dayData.pickupAddress.zip_code}`
            }
          }
          else {
            dayData.pickupAddressDisplay = 'ADDRESS MISSING'
          }
          if (dayData.dropoffAddress) {
            if (dayData.dropoffAddress.apartment) {
              dayData.dropoffAddressDisplay = `${dayData.dropoffAddress.street}, ${dayData.dropoffAddress.apartment}, ${dayData.dropoffAddress.city}, ${dayData.dropoffAddress.state}, ${dayData.dropoffAddress.zip_code}`
            }
            else {
              dayData.dropoffAddressDisplay = `${dayData.dropoffAddress.street}, ${dayData.dropoffAddress.city}, ${dayData.dropoffAddress.state}, ${dayData.dropoffAddress.zip_code}`
            }
          }
          else {
            dayData.dropoffAddressDisplay = 'ADDRESS MISSING'
          }
          if (dayData.pickupTime) {
            dayData.pickupTime = new Date(`1970-01-01T${dayData.pickupTime}.000Z`).toLocaleString('en-US', {
              hour: 'numeric',
              minute: 'numeric',
              hour12: true,
              timeZone: 'UTC',
            })
          }
          if (dayData.dropoffTime) {
            dayData.dropoffTime = new Date(`1970-01-01T${dayData.dropoffTime}.000Z`).toLocaleString('en-US', {
              hour: 'numeric',
              minute: 'numeric',
              hour12: true,
              timeZone: 'UTC',
            })
          }
          content.dayData = {
            dayVariant: false,
            day: dayData,
            weekdays: weekdays,
          }
        }
      }
      else {
        content.dayData = {
          dayVariant: true,
          listDays: [],
        }
      }

      content.effectiveDate = new Date(this.notifyStatusModel.lastNotification.effective_date).toLocaleString('en-US', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        timeZone: 'UTC',
      })

      content.accommodations = []
      if (this.notifyStatusModel.lastNotification.info_aide === 'A') {
        content.accommodations.push('Aide')
      }
      else if (this.notifyStatusModel.lastNotification.info_aide === 'A1:1') {
        content.accommodations.push('Aide 1-to-1')
      }
      if (this.notifyStatusModel.lastNotification.info_wheelchair) content.accommodations.push('Wheelchair')
      if (this.notifyStatusModel.lastNotification.info_solo) content.accommodations.push('Solo')
      if (this.notifyStatusModel.lastNotification.info_enclosed_vehicle) content.accommodations.push('Enclosed Vehicle')
      if (this.notifyStatusModel.lastNotification.info_booster_seat) content.accommodations.push('Booster Seat')
      if (this.notifyStatusModel.lastNotification.info_safety_vest) content.accommodations.push('Safety Vest')
      if (this.notifyStatusModel.lastNotification.info_safety_belt_locking_cover) content.accommodations.push('Safety Belt Locking Cover')
      if (this.notifyStatusModel.lastNotification.info_car_seat) content.accommodations.push('Car Seat')

      return content
    }
  },
  async mounted() {
    this.statusSelected = this.statusFromServer
    this.previousStatusSelected = this.statusFromServer
  },
}
</script>
